import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  Check,
  CheckCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  FileText,
  Hash,
  Lightbulb,
  LucideAngularModule,
  MessageSquare,
  Target,
  Timer,
  XCircle,
} from 'lucide-angular';
import {
  AssignmentApiService,
  SubmissionDetail,
  SubmissionAnswerDetail,
  SubmissionQuestionDetail,
} from '../assignment/services/assignment-api.service';
import { percentageToLetterGrade } from '../../../common/components/grade-distribution-chart/grade-distribution-chart';

@Component({
  selector: 'app-submission-review',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './submission-review.html',
  styleUrl: './submission-review.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubmissionReviewComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly apiService = inject(AssignmentApiService);

  protected readonly icons = {
    AlertTriangle,
    ArrowLeft,
    Calendar,
    Check,
    CheckCircle,
    CheckCircle2,
    Clock,
    XCircle,
    ChevronDown,
    ChevronUp,
    FileText,
    Hash,
    Lightbulb,
    MessageSquare,
    Target,
    Timer,
  };

  private readonly submission$$ =
    new BehaviorSubject<SubmissionDetail | null>(null);
  protected readonly submission$ = this.submission$$.asObservable();

  private readonly loading$$ = new BehaviorSubject<boolean>(true);
  protected readonly loading$ = this.loading$$.asObservable();

  private readonly error$$ = new BehaviorSubject<string | null>(null);
  protected readonly error$ = this.error$$.asObservable();

  protected readonly expandedQuestions = signal<Set<string>>(new Set());

  private readonly OPTION_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  ngOnInit(): void {
    const assignmentId = this.route.snapshot.paramMap.get('id');
    const submissionId = this.route.snapshot.paramMap.get('submissionId');

    if (!assignmentId || !submissionId) {
      this.router.navigate(['/teacher/dashboard/assignments']);
      return;
    }

    this.apiService
      .getSubmissionDetail(assignmentId, submissionId)
      .pipe(
        catchError(() => {
          this.error$$.next(
            'Failed to load submission. It may not exist or you may not have access.',
          );
          return of(null);
        }),
        finalize(() => this.loading$$.next(false)),
      )
      .subscribe((data) => {
        this.submission$$.next(data);
      });
  }

  protected getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  protected getProficiencyLabel(percentage: number | null): string {
    if (percentage === null) return 'Not Graded';
    if (percentage >= 90) return 'Excellent';
    if (percentage >= 80) return 'Proficient';
    if (percentage >= 70) return 'Developing';
    if (percentage >= 60) return 'Approaching';
    return 'Beginning';
  }

  protected getScoreMessage(submission: SubmissionDetail): string {
    const pct = submission.gradePercentage;
    if (pct === null) return 'This submission has not been graded yet.';
    if (pct >= 90)
      return "Outstanding work! The student demonstrated excellent understanding of the material.";
    if (pct >= 80)
      return "Great effort! The student showed strong understanding with some areas to review.";
    if (pct >= 70)
      return "Good progress. The student grasps the core concepts but could benefit from reviewing certain topics.";
    if (pct >= 60)
      return "The student is approaching proficiency. Additional practice on key concepts would be beneficial.";
    return "The student needs additional support. Consider reviewing the fundamental concepts together.";
  }

  protected getQuestionTypeLabel(type: string): string {
    switch (type) {
      case 'multiple_choice':
        return 'Multiple Choice';
      case 'short_answer':
        return 'Short Answer';
      case 'essay':
        return 'Essay';
      default:
        return 'Question';
    }
  }

  protected getQuestionTypeIcon(type: string): any {
    switch (type) {
      case 'multiple_choice':
        return this.icons.CheckCircle;
      case 'short_answer':
        return this.icons.MessageSquare;
      case 'essay':
        return this.icons.FileText;
      default:
        return this.icons.FileText;
    }
  }

  protected getAnswerForQuestion(
    submission: SubmissionDetail,
    questionId: string,
  ): SubmissionAnswerDetail | undefined {
    return submission.answers.find((a) => a.questionId === questionId);
  }

  protected isQuestionCorrect(
    answer: SubmissionAnswerDetail | undefined,
  ): boolean {
    if (!answer) return false;
    return answer.pointsAwarded !== null && answer.pointsAwarded === answer.maxPoints;
  }

  protected isQuestionExpanded(questionId: string): boolean {
    return this.expandedQuestions().has(questionId);
  }

  protected toggleQuestion(questionId: string): void {
    const current = new Set(this.expandedQuestions());
    if (current.has(questionId)) {
      current.delete(questionId);
    } else {
      current.add(questionId);
    }
    this.expandedQuestions.set(current);
  }

  protected getOptionLetter(index: number): string {
    return this.OPTION_LETTERS[index] ?? String(index + 1);
  }

  protected getStudentAnswerText(
    question: SubmissionQuestionDetail,
    answer: SubmissionAnswerDetail | undefined,
  ): string {
    if (!answer) return 'No answer provided';

    if (question.type === 'multiple_choice') {
      if (!answer.selectedOptionId) return 'No answer selected';
      const option = question.options.find(
        (o) => o.id === answer.selectedOptionId,
      );
      return option?.text ?? 'Unknown option';
    }

    return answer.textAnswer || 'No answer provided';
  }

  protected getCorrectAnswerText(question: SubmissionQuestionDetail): string {
    if (question.type === 'multiple_choice') {
      const correct = question.options.find((o) => o.isCorrect);
      return correct?.text ?? 'Not specified';
    }
    return '';
  }

  protected getDuration(submission: SubmissionDetail): string {
    if (!submission.submittedAt || !submission.startedAt) return '—';
    const start = new Date(submission.startedAt).getTime();
    const end = new Date(submission.submittedAt).getTime();
    const diffMs = end - start;
    if (diffMs < 0) return '—';

    const totalSeconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }

  protected getLetterGrade(percentage: number): string {
    return percentageToLetterGrade(percentage);
  }

  protected getGradeBadgeClasses(percentage: number): string {
    const grade = percentageToLetterGrade(percentage);
    switch (grade) {
      case 'A':
        return 'bg-emerald-100 text-emerald-700';
      case 'B':
        return 'bg-green-100 text-green-700';
      case 'C':
        return 'bg-yellow-100 text-yellow-700';
      case 'D':
        return 'bg-orange-100 text-orange-700';
      case 'F':
        return 'bg-red-100 text-red-600';
      default:
        return 'bg-gray-100 text-gray-500';
    }
  }

  protected getScoreColorClass(percentage: number | null): string {
    if (percentage === null) return 'text-gray-500';
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    if (percentage >= 40) return 'text-orange-600';
    return 'text-red-500';
  }

  protected goBack(): void {
    const assignmentId = this.route.snapshot.paramMap.get('id');
    if (assignmentId) {
      this.router.navigate(['/assignment', assignmentId, 'students']);
    } else {
      this.router.navigate(['/teacher/dashboard/assignments']);
    }
  }

  protected getSortedQuestions(
    submission: SubmissionDetail,
  ): SubmissionQuestionDetail[] {
    return [...submission.questions].sort((a, b) => a.order - b.order);
  }
}
